import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  DesApiKeyLink,
  DESCreditRecord,
} from 'src/app/services/machines.service';
import { RegistrationDbService } from 'src/app/services/registration-db.service';
import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import {
  DesCreditPurchase,
  DesCreditPurchasesService,
} from 'src/app/services/des-credit-purchases.service';

export type DesCreditsUpdatedKind = 'credit' | 'link' | 'unlink' | 'create';

@Component({
  selector: 'app-tenant-des-credits',
  templateUrl: './tenant-des-credits.component.html',
  styleUrl: './tenant-des-credits.component.scss',
})
export class TenantDesCreditsComponent implements OnChanges {
  @Input() records: DESCreditRecord[] = [];
  @Input() accountNumber = '';
  @Input() tenantId = '';
  @Input() loading = false;
  @Input() error = '';
  @Input() desApiKeyLink?: DesApiKeyLink;
  @Output() updated = new EventEmitter<DesCreditsUpdatedKind>();

  purchases: DesCreditPurchase[] = [];
  loadingPurchases = false;
  errorPurchases = '';

  @ViewChild('createDialog')
  createDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('editDialog')
  editDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('linkConfirmDialog')
  linkConfirmDialog!: ElementRef<HTMLDialogElement>;

  createCreditInput: string | number = '';
  creating = false;
  createError = '';

  editingRecord: DESCreditRecord | null = null;
  creditInput: string | number = '';
  saving = false;
  saveError = '';
  linking = false;
  linkError = '';
  pendingLinkAction: 'link' | 'unlink' | null = null;
  downloadingId: number | null = null;

  constructor(
    private registrationDbService: RegistrationDbService,
    private errorService: ErrorService,
    private desCreditPurchasesService: DesCreditPurchasesService,
    private toastService: ToastService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tenantId']) {
      this.loadPurchases();
    }
  }

  loadPurchases() {
    if (!this.tenantId) {
      this.purchases = [];
      this.errorPurchases = '';
      this.loadingPurchases = false;
      return;
    }
    this.loadingPurchases = true;
    this.errorPurchases = '';
    this.desCreditPurchasesService.getForTenant(this.tenantId).subscribe({
      next: (res) => {
        this.loadingPurchases = false;
        this.purchases = res.complete || [];
      },
      error: (err) => {
        this.loadingPurchases = false;
        this.purchases = [];
        this.errorPurchases = this.errorService.getErrorMessage(err);
      },
    });
  }

  browseToReceipt(purchase: DesCreditPurchase) {
    if (!purchase.id) {
      return;
    }
    this.router.navigate(['/des-receipt', purchase.id], {
      queryParams: { tenant_id: this.tenantId || purchase.tenant_id },
    });
  }

  purchaseStatus(purchase: DesCreditPurchase): string {
    return purchase.status || 'completed';
  }

  downloadDat(rec: DESCreditRecord) {
    if (!this.accountNumber || this.downloadingId === rec.registration_id) {
      return;
    }
    this.downloadingId = rec.registration_id;
    this.registrationDbService
      .downloadDesCreditDat(this.accountNumber, rec.registration_id)
      .subscribe({
        next: (blob) => {
          this.downloadingId = null;
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `${this.accountNumber}_${rec.registration_id}_des.dat`;
          anchor.click();
          window.URL.revokeObjectURL(url);
        },
        error: async (err) => {
          this.downloadingId = null;
          let message = 'Error downloading des.dat';
          if (err?.error instanceof Blob) {
            try {
              const text = await err.error.text();
              const parsed = JSON.parse(text);
              message = parsed?.error || message;
            } catch {
              message = this.errorService.getErrorMessage(err);
            }
          } else {
            message = this.errorService.getErrorMessage(err);
          }
          this.toastService.addToast('error', message);
        },
      });
  }

  buyCredits(rec: DESCreditRecord) {
    if (!this.tenantId) {
      return;
    }
    this.router.navigate(['/buy-des-credits'], {
      queryParams: {
        tenant_id: this.tenantId,
        registration_id: rec.registration_id,
      },
    });
  }

  displayRecords(): DESCreditRecord[] {
    return this.records.sort((a, b) => a.registration_id - b.registration_id);
  }

  get parsedCreateCredit(): number | null {
    const s = String(this.createCreditInput ?? '').trim();
    if (!/^\d+$/.test(s) || !Number.isSafeInteger(Number(s))) {
      return null;
    }
    return Number(s);
  }

  get canCreate(): boolean {
    return (
      !!this.accountNumber && this.parsedCreateCredit !== null && !this.creating
    );
  }

  openCreate() {
    if (!this.accountNumber || this.creating) {
      return;
    }
    this.createCreditInput = '';
    this.createError = '';
    this.creating = false;
    this.createDialog.nativeElement.showModal();
  }

  closeCreate() {
    if (this.creating) {
      return;
    }
    this.createDialog.nativeElement.close();
    this.createCreditInput = '';
    this.createError = '';
  }

  create() {
    if (!this.canCreate || this.parsedCreateCredit === null) {
      return;
    }
    this.creating = true;
    this.createError = '';
    this.registrationDbService
      .createDesCredit(this.accountNumber, this.parsedCreateCredit)
      .subscribe({
        next: () => {
          this.creating = false;
          this.createDialog.nativeElement.close();
          this.createCreditInput = '';
          this.updated.emit('create');
        },
        error: (err) => {
          this.creating = false;
          this.createError = this.errorService.getErrorMessage(err);
        },
      });
  }

  get parsedCredit(): number | null {
    const s = String(this.creditInput ?? '').trim();
    if (!/^\d+$/.test(s)) {
      return null;
    }
    return Number(s);
  }

  get canSave(): boolean {
    return (
      !!this.editingRecord &&
      !!this.accountNumber &&
      this.parsedCredit !== null &&
      !this.saving &&
      !this.linking
    );
  }

  // Registration db stores credits like "1.00"; portal shows whole numbers.
  wholeCredit(raw?: string | null): number | null {
    const s = String(raw ?? '').trim();
    if (!s) {
      return null;
    }
    const n = Number(s);
    return Number.isFinite(n) ? Math.round(n) : null;
  }

  displayCredit(rec: DESCreditRecord): string {
    const n = this.wholeCredit(rec.credit);
    return n === null ? '—' : String(n);
  }

  get notLinkedMessage(): string {
    return 'API Key is not linked to DES Credit record';
  }

  get multipleLinksWarning(): string {
    return 'Warning: multiple DES Key links exist';
  }

  get showMultipleLinks(): boolean {
    if (this.loading || this.error || this.desApiKeyLink?.lookup_error) {
      return false;
    }
    return this.desApiKeyLink?.multiple_link_records === true;
  }

  get showNotLinked(): boolean {
    if (this.loading || this.error || this.desApiKeyLink?.lookup_error) {
      return false;
    }
    if (this.showMultipleLinks) {
      return false;
    }
    if (this.desApiKeyLink?.linked_to_des_credit) {
      return false;
    }
    return this.records.some((r) => r.linked_to_api_key) === false;
  }

  get showApiKeyLinkActions(): boolean {
    return (
      !!this.desApiKeyLink?.has_api_key && !this.desApiKeyLink?.lookup_error
    );
  }

  get showLinkButton(): boolean {
    return (
      this.showApiKeyLinkActions &&
      !!this.editingRecord &&
      !this.editingRecord.linked_to_api_key
    );
  }

  get showUnlinkButton(): boolean {
    return (
      this.showApiKeyLinkActions && !!this.editingRecord?.linked_to_api_key
    );
  }

  get linkConfirmTitle(): string {
    return this.pendingLinkAction === 'unlink'
      ? 'Unlink API Key?'
      : 'Link API Key?';
  }

  get linkConfirmBody(): string {
    if (this.pendingLinkAction === 'unlink') {
      return 'Unlink this DES credit record from the tenant API key? The ' +
        'link for this DES credit will be removed.';
    }
    return 'Link this DES credit record to the tenant API key? All other ' +
      'linked DES credit records will be unlinked.';
  }

  openEdit(rec: DESCreditRecord) {
    this.editingRecord = rec;
    this.creditInput = this.wholeCredit(rec.credit) ?? '';
    this.saveError = '';
    this.saving = false;
    this.linkError = '';
    this.linking = false;
    this.pendingLinkAction = null;
    this.editDialog.nativeElement.showModal();
  }

  closeEdit() {
    this.linking = false;
    this.closeLinkConfirm();
    this.editDialog.nativeElement.close();
    this.editingRecord = null;
    this.creditInput = '';
    this.saveError = '';
    this.saving = false;
    this.linkError = '';
    this.pendingLinkAction = null;
  }

  openLinkConfirm(action: 'link' | 'unlink') {
    this.pendingLinkAction = action;
    this.linkError = '';
    this.linkConfirmDialog.nativeElement.showModal();
  }

  closeLinkConfirm() {
    if (this.linking) {
      return;
    }
    this.linkConfirmDialog?.nativeElement.close();
    this.pendingLinkAction = null;
    this.linkError = '';
  }

  confirmLinkAction() {
    if (
      !this.pendingLinkAction ||
      !this.editingRecord ||
      !this.accountNumber ||
      this.linking
    ) {
      return;
    }
    const kind: DesCreditsUpdatedKind =
      this.pendingLinkAction === 'unlink' ? 'unlink' : 'link';
    const linked = this.pendingLinkAction === 'link';
    this.linking = true;
    this.linkError = '';
    this.registrationDbService
      .updateDesCreditApiKeyLink(
        this.accountNumber,
        this.editingRecord.registration_id,
        linked
      )
      .subscribe({
        next: () => {
          this.linking = false;
          this.closeLinkConfirm();
          this.editDialog.nativeElement.close();
          this.editingRecord = null;
          this.creditInput = '';
          this.updated.emit(kind);
        },
        error: (err) => {
          this.linking = false;
          this.linkError = this.errorService.getErrorMessage(err);
        },
      });
  }

  save() {
    if (!this.canSave || !this.editingRecord || this.parsedCredit === null) {
      return;
    }
    this.saving = true;
    this.saveError = '';
    this.registrationDbService
      .updateDesCredit(
        this.accountNumber,
        this.editingRecord.registration_id,
        this.parsedCredit
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.closeEdit();
          this.updated.emit('credit');
        },
        error: (err) => {
          this.saving = false;
          this.saveError = this.errorService.getErrorMessage(err);
        },
      });
  }
}
